import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CardSkeleton, SelectTriggerSkeleton, StatCardsSkeleton } from '@/components/core';
import { useAuthStore } from '@/state/store/auth';
import { ComparisonTable } from '../../components/comparison-table/comparison-table';
import { EmployeePicker } from '../../components/employee-picker/employee-picker';
import { HealthStrip } from '../../components/health-strip/health-strip';
import { HeroSummary } from '../../components/hero-summary/hero-summary';
import { KpiStrip } from '../../components/kpi-strip/kpi-strip';
import { QuantitativeScoring } from '../../components/quantitative-scoring/quantitative-scoring';
import { ScoreOverview } from '../../components/score-overview/score-overview';
import { StrengthsImprovements } from '../../components/strengths-improvements/strengths-improvements';
import { WeightedCategoryBars } from '../../components/weighted-category-bars/weighted-category-bars';
import { useDashboardAccess, useGetPerformanceDashboard } from '../../hooks/use-dashboard';

export const PerformanceDashboardPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const currentUserId = useAuthStore((state) => state.user?.itemId ?? '');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  useEffect(() => {
    setSelectedEmployeeId(searchParams.get('userId') ?? '');
  }, [searchParams]);

  const access = useDashboardAccess(selectedEmployeeId);
  const { data: dashboard, isLoading } = useGetPerformanceDashboard(
    selectedEmployeeId,
    access.canView
  );

  // The dashboard's sections (summary, KPI strip, the two charts) as placeholders.
  const dashboardSkeleton = (
    <div className="flex flex-col gap-4" aria-busy="true">
      <CardSkeleton lines={2} />
      <StatCardsSkeleton count={4} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CardSkeleton bodyClassName="h-56 w-full" />
        <CardSkeleton bodyClassName="h-56 w-full" />
      </div>
    </div>
  );

  if (access.isResolving) {
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-lg font-semibold">{t('PERFORMANCE_DASHBOARD')}</h1>
          <SelectTriggerSkeleton className="w-full sm:w-72" />
        </div>
        {selectedEmployeeId && dashboardSkeleton}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-lg font-semibold">{t('PERFORMANCE_DASHBOARD')}</h1>
        {access.scope !== 'none' && (
          <EmployeePicker
            value={selectedEmployeeId}
            onChange={setSelectedEmployeeId}
            departmentId={access.scope === 'unit' ? access.departmentId : undefined}
            excludeId={currentUserId}
          />
        )}
      </div>

      {access.scope === 'none' && (
        <p className="text-sm text-muted-foreground">{t('NOT_AUTHORIZED_TO_VIEW_DASHBOARD')}</p>
      )}
      {access.scope !== 'none' && !selectedEmployeeId && (
        <p className="text-sm text-muted-foreground">{t('SELECT_EMPLOYEE_TO_VIEW_DASHBOARD')}</p>
      )}
      {access.scope !== 'none' && selectedEmployeeId && !access.canView && (
        <p className="text-sm text-muted-foreground">{t('CANNOT_VIEW_THIS_DASHBOARD')}</p>
      )}

      {access.canView && isLoading && dashboardSkeleton}
      {access.canView && !isLoading && !dashboard && (
        <p className="text-sm text-muted-foreground">{t('NO_REVIEW_DATA_AVAILABLE')}</p>
      )}

      {dashboard && access.canView && (
        <>
          <HeroSummary dashboard={dashboard} />
          <KpiStrip dashboard={dashboard} />
          {dashboard.IsQuantitativeEligible && <HealthStrip dashboard={dashboard} />}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
            <ScoreOverview dashboard={dashboard} />
            <WeightedCategoryBars dashboard={dashboard} />
          </div>

          {dashboard.IsQuantitativeEligible && <QuantitativeScoring dashboard={dashboard} />}
          <StrengthsImprovements dashboard={dashboard} />
          <ComparisonTable dashboard={dashboard} />
        </>
      )}
    </div>
  );
};
